import React from 'react';
import type { AxiosResponse } from 'axios';

import { getCountryApiParam } from '../i18n/country';
import type { Languages } from '../i18n/i18n.language-config';
import { usePlatformLanguage } from '../utils/hooks/usePlatformLanguage';
import { fetchNaicsCodes } from './NaicsService';

/**
 * Search industry codes, scoped by default to the active platform's country.
 *
 * The four industry autocompletes (NaicsCodeAutoComplete's two exports,
 * SicIndustryAutoComplete and NaicsIndustryAutoComplete) each carried an identical copy of this
 * block, differing only in how they shaped the response — so it lived here instead. Each passes its
 * own module-scope mapper, which keeps the returned callback stable across renders.
 *
 * The copies also each carried an `if (!country) console.warn(...)` guard and a
 * `...(country && { country })` conditional spread. Both were dead: the country comes from the
 * platform language, and every platform language resolves. Dropping them is why `country` is now
 * sent unconditionally — which is what already happened in practice.
 *
 * @param mapContent must be module-scope (or otherwise stable) — it is a dependency of the memo.
 * @param language the taxonomy to search, when it is not the platform's. A caller that picks its
 *   mapper from something other than the platform language — the account's own country, say — has
 *   to scope the query the same way, or it maps one country's catalog with another's mapper:
 *   `toSicOptions` over NAICS rows drops every row for want of a `sicCode` and the picker comes
 *   back empty (OMNI-999).
 */
export const useIndustryCodeSearch = <T>(
  mapContent: (content: any[]) => T,
  language?: Languages
) => {
  const platformLanguage = usePlatformLanguage();
  const country = getCountryApiParam(language ?? platformLanguage);

  return React.useCallback(
    (search: string): Promise<T> =>
      fetchNaicsCodes({ search, dup: 0, country }).then((resp: AxiosResponse) =>
        mapContent(resp?.data?.content ?? [])
      ),
    [country, mapContent]
  );
};
